// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import {
  types as t,
  usePropsValidation,
} from '../validation/runtime-checks/index.js';
import { Text } from '../Text.js';
import Latex from '../Latex.js';
import { List } from '../List.js';
import Image from '../Image.js';
import DraftImage from '../DraftImage.js';
import BoxAndWhiskerPlot from '../BoxAndWhiskerPlot.js';
import { CartesianPlane } from '../CartesianPlane.js';
import { NumberLine } from '../NumberLine.js';
import { StemAndLeafPlot } from '../StemAndLeafPlot.js';
import TabularData from '../TabularData.js';
import { TwoColumnProof } from '../TwoColumnProof.js';
import { AlgebraicWorking } from '../AlgebraicWorking.js';
import { EngageNoteCollapsibleInternal } from './EngageNoteCollapsibleInternal.js';
import { productColor } from '../designSystem.js';
import { LatexTextAtom } from '../LatexTextAtom.js';

// The set of components that authors are allowed to use when writing content
// at either the EANStrategyPanel level, or at the EANStrategyDetail level
type ContentElement =
  | Element<typeof Text>
  | Element<typeof Latex>
  | Element<typeof LatexTextAtom>
  | Element<typeof List>
  | Element<typeof Image>
  | Element<typeof DraftImage>
  // probably only need those above this line
  | Element<typeof BoxAndWhiskerPlot>
  | Element<typeof CartesianPlane>
  | Element<typeof NumberLine>
  | Element<typeof StemAndLeafPlot>
  | Element<typeof TabularData>
  | Element<typeof TwoColumnProof>
  | Element<typeof AlgebraicWorking>;
const contentElementSpecList = [
  t.elementType(Text),
  t.elementType(Latex),
  t.elementType(LatexTextAtom),
  t.elementType(List),
  t.elementType(Image),
  t.elementType(DraftImage),
  // probably only need those above this line
  t.elementType(BoxAndWhiskerPlot),
  t.elementType(CartesianPlane),
  t.elementType(NumberLine),
  t.elementType(StemAndLeafPlot),
  t.elementType(TabularData),
  t.elementType(TwoColumnProof),
  t.elementType(AlgebraicWorking),
];

export function EANStrategyPanel(props: {|
  children: ChildrenArray<ContentElement | Element<typeof EANStrategyDetail>>,
|}) {
  const { failedValidation } = usePropsValidation(props, EANStrategyPanel);
  if (failedValidation) return null;
  const children = React.Children.toArray(props.children);
  return (
    <EngageNoteCollapsibleInternal title="Anticipated strategies">
      {children.map((child, i) => (
        <React.Fragment key={i}>
          <div style={{ color: productColor.shuttleGrey }}>{child}</div>
          {i !== children.length - 1 && <div style={{ height: 16 }} />}
        </React.Fragment>
      ))}
    </EngageNoteCollapsibleInternal>
  );
}
EANStrategyPanel.spec = {
  children: t.childrenArray(
    t.unionOfTypes(...contentElementSpecList, t.elementType(EANStrategyDetail)),
  ),
};

// this component is used in both the strategy panel
// and the discuss panel, they should look identical always
export function EANStrategyDetail(props: {|
  type: string,
  subtype: string,
  children: ChildrenArray<
    | Element<typeof Text>
    | Element<typeof Latex>
    | Element<typeof List>
    | Element<typeof Image>
    | Element<typeof DraftImage>
    // probably only need those above this line
    | Element<typeof BoxAndWhiskerPlot>
    | Element<typeof CartesianPlane>
    | Element<typeof NumberLine>
    | Element<typeof StemAndLeafPlot>
    | Element<typeof TabularData>
    | Element<typeof TwoColumnProof>
    | Element<typeof AlgebraicWorking>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(props, EANStrategyDetail);
  if (failedValidation) return null;
  const cellStyle = {
    display: 'table-cell',
    verticalAlign: 'top',
    textAlign: 'left',
  };
  return (
    <div style={{ display: 'table', tableLayout: 'fixed' }}>
      <div style={{ width: 20 * 5 + 4 * 5, ...cellStyle }}>
        <div style={{ fontWeight: 700 }}>{props.type}</div>
        <div style={{ marginTop: 8 }}>{props.subtype}</div>
      </div>
      <div style={{ width: 40 }} />
      <div style={{ width: 20 * 20 + 4 * 20, ...cellStyle }}>
        {props.children}
      </div>
      <div style={{ width: 20 + 4, ...cellStyle }} />
    </div>
  );
}
EANStrategyDetail.spec = {
  type: t.string,
  subtype: t.string,
  children: t.childrenArray(t.unionOfTypes(...contentElementSpecList)),
};
